import React, { useState, useEffect, useMemo } from "react";
import FloatingPanelService from "../FloatingPanel";
import { capitalize } from "../../../utils/FormatUtils";

export type Item = {
  title: string;
  component?: () => JSX.Element;
  hash?: string;
  hasItems?: boolean;
  hidden?: boolean;
  items?: Item[];
};

const deriveHash = (it: Item) => it.hash || it.title.replace(/\s+/g, "");

const readLocationHash = () =>
  decodeURIComponent(window.location.hash.substring(1));

const visibleItems = (list: Item[]) => list.filter((it) => !it.hidden);

const VerticalMenu: React.FC<{ items: Item[]; title: string }> = ({
  items,
  title
}) => {
  const componentMap = useMemo(() => {
    const map: Record<string, () => JSX.Element> = {};
    const walk = (list: Item[]) =>
      list.forEach((it) => {
        const h = deriveHash(it);
        if (it.component) map[h] = it.component;
        if (it.items) walk(it.items);
      });
    walk(items);
    return map;
  }, [items]);

  const hashes = useMemo(() => Object.keys(componentMap), [componentMap]);

  const initial = readLocationHash() || hashes[0] || "";
  const [activeHash, setActiveHash] = useState(initial);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!componentMap[activeHash] && hashes.length) {
      setActiveHash(hashes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    const onHashChange = () => setActiveHash(readLocationHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const toggle = (h: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(h) ? next.delete(h) : next.add(h);
      return next;
    });

  const handleClick = (it: Item) => {
    const h = deriveHash(it);
    setActiveHash(h);
    window.location.hash = h;
    setTimeout(() => FloatingPanelService.hidePanel(), 0);
  };

  const renderItems = (list: Item[], level = 0): JSX.Element[] =>
    visibleItems(list).map((it) => {
      const h = deriveHash(it);
      const isParent = Array.isArray(it.items) && it.items.length > 0;
      const isOpen = expanded.has(h);
      const pad = 16 + level * 20;

      return (
        <li className="nav-item" key={h}>
          <button
            className={`btn btn-link ${!level ? "bold " : ""} ${
              isParent ? "expand-btn" : "leaf-btn"
            } ${!isParent && h === activeHash ? "active" : ""}`}
            style={{ paddingLeft: pad }}
            onClick={() => (isParent ? toggle(h) : handleClick(it))}
          >
            {isParent && (
              <span className="expand-icon">
                <i
                  className={
                    "fa-solid fa-fw " +
                    (!isOpen ? "fa-chevron-right" : "fa-chevron-down")
                  }
                ></i>
              </span>
            )}
            {it.title}
          </button>

          {isParent && (
            <ul className={`submenu${isOpen ? " expanded" : ""}`}>
              {renderItems(it.items!, level + 1)}
            </ul>
          )}
        </li>
      );
    });

  const ActiveComponent = componentMap[activeHash];

  return (
    <div className="d-flex">
      <nav className="nav-sidebar">
        <div className="menu-header">
          <h2 className="menu-title">
            {title.split(" ").map(capitalize).join(" ")}
          </h2>
          <hr className="menu-separator" />
        </div>
        <ul className="nav flex-column">{renderItems(items)}</ul>
      </nav>

      <div
        className="tab-content p-3 w-100"
        style={{ minHeight: "85vh", maxHeight: "85vh", overflowY: "auto" }}
      >
        {ActiveComponent ? <ActiveComponent /> : null}
      </div>
    </div>
  );
};

export default VerticalMenu;
